import { IText, util, Point, Control, Object as FabricObject } from 'fabric';

type FabricObjectWithGrid = IText & {
  gridSize: number;
  gridPoints: { x: number; y: number; ox: number; oy: number }[][];
  isWarped: () => boolean;
  _render_original?: (ctx: CanvasRenderingContext2D) => void;
  // for caching
  _warpTexture: HTMLCanvasElement | null;
  textureDirty: boolean;
  // for overriding
  set_original?(key: any, value?: any): FabricObjectWithGrid;
  setCoords_original?(): void;
};

type CustomControl = Control & {
  corner: string;
};

function catmullRom(p0: {x:number, y:number}, p1: {x:number, y:number}, p2: {x:number, y:number}, p3: {x:number, y:number}, t: number, s = 0.5) {
  const t2 = t * t, t3 = t2 * t;
  const a = 2*t3 - 3*t2 + 1;
  const b = t3 - 2*t2 + t;
  const c = -2*t3 + 3*t2;
  const d = t3 - t2;
  const t1x = s * (p2.x - p0.x);
  const t1y = s * (p2.y - p0.y);
  const t2x = s * (p3.x - p1.x);
  const t2y = s * (p3.y - p1.y);
  const x = a * p1.x + b * t1x + c * p2.x + d * t2x;
  const y = a * p1.y + b * t1y + c * p2.y + d * t2y;
  return { x, y };
}

function drawTexturedTriangle(
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    p0: Point, p1: Point, p2: Point,
    uv0: Point, uv1: Point, uv2: Point
) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    ctx.lineTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.closePath();
    ctx.clip();

    const denom = uv0.x * (uv1.y - uv2.y) + uv1.x * (uv2.y - uv0.y) + uv2.x * (uv0.y - uv1.y);
    if (denom === 0) {
        ctx.restore();
        return;
    }

    const a = (p0.x * (uv1.y - uv2.y) + p1.x * (uv2.y - uv0.y) + p2.x * (uv0.y - uv1.y)) / denom;
    const b = (p0.y * (uv1.y - uv2.y) + p1.y * (uv2.y - uv0.y) + p2.y * (uv0.y - uv1.y)) / denom;
    const c = (p0.x * (uv2.x - uv1.x) + p1.x * (uv0.x - uv2.x) + p2.x * (uv1.x - uv0.x)) / denom;
    const d = (p0.y * (uv2.x - uv1.x) + p1.y * (uv0.x - uv2.x) + p2.y * (uv1.x - uv0.x)) / denom;
    const e = p0.x - a * uv0.x - c * uv0.y;
    const f = p0.y - b * uv0.x - d * uv0.y;

    ctx.transform(a, b, c, d, e, f);
    ctx.drawImage(image, 0, 0);
    ctx.restore();
}

function getWarpedAABB(this: FabricObjectWithGrid): { min: Point; max: Point } {
    if (!this.isWarped()) {
        const halfWidth = this.width / 2;
        const halfHeight = this.height / 2;
        return {
            min: new Point(-halfWidth, -halfHeight),
            max: new Point(halfWidth, halfHeight),
        };
    }

    const points = this.gridPoints.flat();
    if (points.length === 0) {
        const halfWidth = this.width / 2;
        const halfHeight = this.height / 2;
        return {
            min: new Point(-halfWidth, -halfHeight),
            max: new Point(halfWidth, halfHeight),
        };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const p of points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    }
    
    return {
        min: new Point(minX, minY),
        max: new Point(maxX, maxY),
    };
}

function customRender(this: FabricObjectWithGrid, ctx: CanvasRenderingContext2D) {
    if (!this.isWarped()) {
        return this._render_original?.(ctx);
    }

    if (this.textureDirty || !this._warpTexture) {
        const zoom = this.canvas ? this.canvas.getZoom() : 1;
        const scaleX = this.scaleX * zoom;
        const scaleY = this.scaleY * zoom;

        const offscreenCanvas = util.createCanvasElement();
        const { width, height } = this;
        offscreenCanvas.width = Math.ceil(width * scaleX);
        offscreenCanvas.height = Math.ceil(height * scaleY);
        const offscreenCtx = offscreenCanvas.getContext('2d')!;
        
        offscreenCtx.scale(scaleX, scaleY);
        offscreenCtx.translate(width / 2, height / 2);
        this._render_original?.(offscreenCtx);
        this._warpTexture = offscreenCanvas;
        this.textureDirty = false;
    }

    const texture = this._warpTexture;
    if (!texture) return;

    const gridSize = this.gridSize;
    const meshCols = gridSize - 1;
    const meshRows = gridSize - 1;
    const { width, height } = this;
    
    const zoom = this.canvas ? this.canvas.getZoom() : 1;
    const scaleX = this.scaleX * zoom;
    const scaleY = this.scaleY * zoom;

    for (let r = 0; r < meshRows; r++) {
        for (let c = 0; c < meshCols; c++) {
            const p00 = this.gridPoints[r][c];
            const p01 = this.gridPoints[r][c + 1];
            const p10 = this.gridPoints[r + 1][c];
            const p11 = this.gridPoints[r + 1][c + 1];

            const uv00 = new Point((p00.ox + width / 2) * scaleX, (p00.oy + height / 2) * scaleY);
            const uv01 = new Point((p01.ox + width / 2) * scaleX, (p01.oy + height / 2) * scaleY);
            const uv10 = new Point((p10.ox + width / 2) * scaleX, (p10.oy + height / 2) * scaleY);
            const uv11 = new Point((p11.ox + width / 2) * scaleX, (p11.oy + height / 2) * scaleY);
            
            const point00 = new Point(p00.x, p00.y);
            const point01 = new Point(p01.x, p01.y);
            const point10 = new Point(p10.x, p10.y);
            const point11 = new Point(p11.x, p11.y);

            drawTexturedTriangle(ctx, texture, point00, point10, point01, uv00, uv10, uv01);
            drawTexturedTriangle(ctx, texture, point11, point01, point10, uv11, uv01, uv10);
        }
    }
}

function renderGridPointIcon(ctx: CanvasRenderingContext2D, left: number, top: number, styleOverride: any, fabricObject: any) {
  ctx.save();
  ctx.translate(left, top);
  ctx.rotate(util.degreesToRadians(fabricObject.angle));
  ctx.beginPath();
  ctx.arc(0, 0, 5, 0, 2 * Math.PI);
  ctx.fillStyle = 'red';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();
}

function gridPointActionHandler(eventData: MouseEvent, transform: any, x: number, y: number) {
  const target = transform.target as FabricObjectWithGrid;
  const corner = transform.corner;
  const [_, r_str, c_str] = corner.split('_');
  const r = parseInt(r_str), c = parseInt(c_str);

  const point = util.transformPoint(
    new Point(x, y),
    util.invertTransform(target.calcTransformMatrix())
  );
  
  target.gridPoints[r][c].x = point.x;
  target.gridPoints[r][c].y = point.y;
  
  return true;
}

function gridPointPositionHandler(this: CustomControl, dim: any, finalMatrix: any, fabricObject: any) {
    const r = parseInt(this.corner.split('_')[1]);
    const c = parseInt(this.corner.split('_')[2]);
    const point = (fabricObject as FabricObjectWithGrid).gridPoints[r][c];
    return util.transformPoint(new Point(point.x, point.y), fabricObject.calcTransformMatrix());
}

function cloneControls(controls: { [key: string]: Control }): { [key: string]: Control } {
    const newControls: { [key: string]: Control } = {};
    for (const key in controls) {
        if (controls.hasOwnProperty(key)) {
            const control = controls[key];
            newControls[key] = new Control(control as any);
        }
    }
    return newControls;
}

export function enableTextWarping(textObject: IText, gridSize: number, gridPoints: any[] = []) {
    const obj = textObject as FabricObjectWithGrid;

    if (!obj._render_original) {
        // First time setup
        obj._render_original = obj._render.bind(obj);
        obj._render = customRender as any;

        obj.setCoords_original = obj.setCoords.bind(obj);
        obj.setCoords = function(this: FabricObjectWithGrid) {
            this.setCoords_original?.();
            if (this.isWarped()) {
                const aabb = getWarpedAABB.call(this);

                const originalWidth = this.width;
                const originalHeight = this.height;
                const originalLeft = this.left;
                const originalTop = this.top;

                const newWidth = aabb.max.x - aabb.min.x;
                const newHeight = aabb.max.y - aabb.min.y;
                const centerX = (aabb.min.x + aabb.max.x) / 2;
                const centerY = (aabb.min.y + aabb.max.y) / 2;

                const centerOffset = new Point(centerX, centerY);
                centerOffset.x *= this.scaleX;
                centerOffset.y *= this.scaleY;
                const rotatedCenterOffset = util.rotatePoint(centerOffset, new Point(0,0), util.degreesToRadians(this.angle));

                (this as any).width = newWidth;
                (this as any).height = newHeight;
                (this as any).left = originalLeft + rotatedCenterOffset.x;
                (this as any).top = originalTop + rotatedCenterOffset.y;

                this.setCoords_original?.();

                (this as any).width = originalWidth;
                (this as any).height = originalHeight;
                (this as any).left = originalLeft;
                (this as any).top = originalTop;
            }
        }.bind(obj);

        obj.set_original = obj.set.bind(obj);
        obj.set = function(this: FabricObjectWithGrid, key: any, value?: any) {
            const watchedProps = ['text', 'fontSize', 'fontFamily', 'fontWeight', 'fill', 'stroke', 'strokeWidth', 'textBackgroundColor'];
            let dirty = false;
            if (typeof key === 'object') {
                for (const k in key) {
                    if (watchedProps.includes(k)) {
                        dirty = true;
                        break;
                    }
                }
            } else if (watchedProps.includes(key)) {
                dirty = true;
            }
            if (dirty) {
                this.textureDirty = true;
            }
            this.set_original?.(key, value);
            return this;
        }.bind(obj);
    }

    if (obj.gridSize !== gridSize) {
        obj.gridSize = gridSize;
        obj.gridPoints = [];
        const w = obj.width * 1.2;
        const h = obj.height * 1.2;
        const localLeft = -w / 2;
        const localTop = -h / 2;
        const cellW = w / (gridSize - 1);
        const cellH = h / (gridSize - 1);

        for (let r = 0; r < gridSize; r++) {
            obj.gridPoints[r] = [];
            for (let c = 0; c < gridSize; c++) {
                if (gridPoints && gridPoints.length > 0 && gridPoints[r] && gridPoints[r][c]) {
                    obj.gridPoints[r][c] = gridPoints[r][c];
                } else {
                    const x = localLeft + c * cellW;
                    const y = localTop + r * cellH;
                    obj.gridPoints[r][c] = { x, y, ox: x, oy: y };
                }
            }
        }
    } else if (gridPoints && gridPoints.length > 0) {
        obj.gridPoints = gridPoints;
    }

    obj.textureDirty = true;

    obj.isWarped = function(this: FabricObjectWithGrid) {
        return !!this.gridSize;
    }.bind(obj);

    // Start with a clone of default controls to keep scale/rotate functionality
    obj.controls = cloneControls(FabricObject.prototype.controls);

    for (let r = 0; r < gridSize; r++) {
        for (let c = 0; c < gridSize; c++) {
            const cornerName = `grid_${r}_${c}`;
            obj.controls[cornerName] = new Control({
                positionHandler: gridPointPositionHandler,
                actionHandler: gridPointActionHandler,
                render: renderGridPointIcon,
                corner: cornerName,
            } as any);
        }
    }
    
    // As in Canvas.tsx, hide the linear scaling controls
    obj.setControlsVisibility?.({ ml: false, mr: false, mt: false, mb: false });
    
    textObject.dirty = true;
    textObject.canvas?.requestRenderAll();
}

export function disableTextWarping(textObject: IText) {
    const obj = textObject as FabricObjectWithGrid;

    if (obj._render_original) {
        obj._render = obj._render_original;
        delete obj._render_original;
    }
    if (obj.setCoords_original) {
        obj.setCoords = obj.setCoords_original;
        delete obj.setCoords_original;
    }
    if (obj.set_original) {
        obj.set = obj.set_original;
        delete obj.set_original;
    }

    if (obj.controls) {
        Object.keys(obj.controls).forEach(key => {
            if (key.startsWith('grid_')) {
                delete obj.controls[key];
            }
        });
    }

    obj.gridSize = 0;
    obj.gridPoints = [];
    if (typeof obj.isWarped === 'function') {
      obj.isWarped = () => false;
    }

    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}